import { router } from 'expo-router';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Image, Platform, Text } from 'react-native';
import {
    BodyLargeRegular,
    Button,
    ButtonVariant,
    H2Regular,
    isWeb,
    useThemeColors,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import appIcon from '../../../assets/icon.png';
import { StorageKeys } from '../../common/constants';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useStorage } from '../../common/providers/StorageProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import useRegisterForPushNotifications from '../../notifications/hooks/useRegisterForPushNotifications';
import OnboardingFullScreenView from './OnboardingFullScreenView';

interface DummyNotificationProps {
    image: ReactNode;
    title: string;
    subText: string;
    time: string;
}

const DummyNotification = ({
    image,
    title,
    subText,
    time,
}: DummyNotificationProps) => {
    const { getColor } = useThemeColors();
    const shadowStyle = Platform.select({
        ios: {
            shadowColor: 'rgba(0, 0, 0, 0.03)',
            shadowOffset: {
                width: 0,
                height: 5,
            },
            shadowOpacity: 0.1,
            shadowRadius: 5,
        },
        android: {
            elevation: 5,
        },
    });

    return (
        <XStack
            borderRadius={24}
            paddingTop={14}
            paddingRight={18}
            paddingBottom={12}
            paddingLeft={16}
            gap={10}
            backgroundColor="$white"
            width="100%"
            minHeight={80}
            style={{ ...shadowStyle }}
        >
            <View alignItems="center" justifyContent="center">
                {image}
            </View>
            <YStack flex={1}>
                <XStack justifyContent="space-between" alignItems="center">
                    <Text
                        style={{
                            fontSize: 14,
                            fontFamily: 'SF Pro Display',
                            fontWeight: '500',
                            lineHeight: 19,
                        }}
                    >
                        {title}
                    </Text>
                    <Text
                        style={{
                            fontSize: 12,
                            fontFamily: 'SF Pro Display',
                            fontWeight: '400',
                            color: getColor('$gray900'),
                            lineHeight: 19,
                        }}
                    >
                        {time}
                    </Text>
                </XStack>
                <Text
                    style={{
                        fontSize: 14,
                        fontFamily: 'SF Pro Display',
                        fontWeight: '400',
                        letterSpacing: -1,
                        color: getColor('$gray1000'),
                        lineHeight: 19,
                    }}
                >
                    {subText}
                </Text>
            </YStack>
        </XStack>
    );
};

const OnboardingNotifications = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`Onboarding/LoginNotifications`);

    const user = useAuthedUser();
    const { guide } = user.membership ?? {};
    const guideName = guide?.firstName ?? 'your Guide';
    const guideImageUrl = guide?.profileImageUrl ?? undefined;

    const toast = useToastController();
    const storage = useStorage();
    const { registerForPushNotifications } = useRegisterForPushNotifications();

    useEffect(() => {
        if (!isWeb) {
            storage.setBool(StorageKeys.HAS_SEEN_NOTIFICATIONS_PROMPT, true);
        }
    }, []);

    const onEnableNotifications = async () => {
        try {
            await registerForPushNotifications();
        } catch (e) {
            toast.show('Failed to register device for push notifications.', {
                duration: 2000,
            });
        }

        goToNextPage();
    };

    const goToNextPage = () => {
        router.navigate('/home');
    };

    const dummyCardData = [
        {
            image: (
                <View overflow="visible" height={36} width={36}>
                    <Image
                        source={{ uri: guideImageUrl }}
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: 20,
                            paddingBottom: 2,
                            marginRight: 2,
                        }}
                    />
                    <View position="absolute" bottom={0} right={0}>
                        <Image
                            source={appIcon}
                            style={{
                                width: 15,
                                height: 15,
                                borderRadius: 4,
                            }}
                        />
                    </View>
                </View>
            ),
            title: `Message from ${guideName}`,
            subText:
                'Looking forward to catching up with you during our session tomorrow!',
            time: 'Now',
        },
        {
            image: (
                <Image
                    source={appIcon}
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                    }}
                />
            ),
            title: 'Upcoming session!',
            subText: `Reminder - you have a session tomorrow at 11:30 AM with ${guideName}`,
            time: '11:00 AM',
        },
        {
            image: (
                <Image
                    source={appIcon}
                    style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                    }}
                />
            ),
            title: 'Connect institutions',
            subText: `Remember to connect your accounts before your session with ${guideName}`,
            time: '5:30 PM',
        },
    ];

    return (
        <OnboardingFullScreenView hasHeader={false} scrollable={false}>
            <YStack
                width="100%"
                paddingBottom={24}
                flex={1}
                alignItems="center"
                justifyContent="space-between"
            >
                <YStack
                    width="100%"
                    gap={60}
                    alignItems="center"
                    maxWidth={500}
                    alignSelf="center"
                >
                    <YStack width="80%" alignItems="center">
                        <H2Regular paddingTop={80} paddingBottom={8}>
                            Notifications
                        </H2Regular>
                        <BodyLargeRegular textAlign="center">
                            Receive push notifications about messages from your
                            Guide and upcoming sessions.
                        </BodyLargeRegular>
                    </YStack>
                    <View alignItems="center" width="100%">
                        <YStack gap={10} zIndex={10}>
                            {dummyCardData.map((data, index) => {
                                const { image, title, subText, time } = data;
                                return (
                                    <DummyNotification
                                        key={index}
                                        image={image}
                                        title={title}
                                        subText={subText}
                                        time={time}
                                    />
                                );
                            })}
                        </YStack>
                        <View
                            width="100%"
                            scale={0.95}
                            opacity={0.5}
                            position="absolute"
                            bottom={-12}
                            left={0}
                            zIndex={5}
                        >
                            <DummyNotification
                                image={<></>}
                                title=""
                                subText=""
                                time=""
                            />
                        </View>
                        <View
                            width="100%"
                            scale={0.9}
                            opacity={0.3}
                            position="absolute"
                            bottom={-24}
                            left={0}
                            zIndex={5}
                        >
                            <DummyNotification
                                image={<></>}
                                title=""
                                subText=""
                                time=""
                            />
                        </View>
                    </View>
                </YStack>
                <YStack width="100%" gap={8} maxWidth={500} alignSelf="center">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text="Enable notifications"
                        onPress={onEnableNotifications}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        text="Skip for now"
                        onPress={goToNextPage}
                    />
                </YStack>
            </YStack>
        </OnboardingFullScreenView>
    );
};

export default OnboardingNotifications;
